import { useState, type FormEvent } from 'react'

import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorText,
  Input,
  PageSpinner,
  Select,
  Sheet,
  useToast,
} from '@/components/ui'
import { formatDate, formatDayTitle } from '@/lib/format'
import { todayIso } from '@/lib/periods'

import {
  useAddCondition,
  useBodyAreas,
  useCloseCondition,
  useConditions,
  useDeleteCondition,
  useDeleteWellbeing,
  useSaveWellbeing,
  useWellbeing,
  type Condition,
  type ConditionKind,
  type Wellbeing,
} from './api'
import { fieldError, otherError } from './logic'
import { DeleteButton, Rating, TabToolbar } from './parts'

const WELLBEING_CAPTIONS = ['плохо', 'так себе', 'нормально', 'хорошо', 'отлично'] as const
const ENERGY_CAPTIONS = ['нет сил', 'мало', 'средне', 'много', 'заряд'] as const
const KIND_LABEL: Record<ConditionKind, string> = { injury: 'Травма', illness: 'Болезнь', pain: 'Боль' }
const SEVERITY_LABEL = ['', 'лёгкая', 'средняя', 'тяжёлая'] as const

function WellbeingForm({ existing, onDone }: { existing: Wellbeing | undefined; onDone: () => void }) {
  const toast = useToast()
  const save = useSaveWellbeing()
  const [day, setDay] = useState(existing?.day ?? todayIso())
  const [wellbeing, setWellbeing] = useState<number | null>(existing?.wellbeing ?? null)
  const [energy, setEnergy] = useState<number | null>(existing?.energy ?? null)
  const [note, setNote] = useState(existing?.note ?? '')
  const [missing, setMissing] = useState(false)

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (wellbeing === null) {
      setMissing(true)
      return
    }
    save.mutate(
      { day, data: { wellbeing, energy, note: note.trim() || null } },
      {
        onSuccess: () => {
          toast.show({ message: 'Самочувствие записано', tone: 'accent' })
          onDone()
        },
      },
    )
  }

  return (
    <form onSubmit={submit} className="grid gap-4" noValidate>
      <Input label="День" type="date" value={day} max={todayIso()} onChange={(e) => setDay(e.target.value)} />
      <Rating
        label="Самочувствие"
        value={wellbeing}
        onChange={(v) => {
          setWellbeing(v)
          setMissing(false)
        }}
        captions={WELLBEING_CAPTIONS}
        error={missing ? 'Выбери оценку от 1 до 5' : fieldError(save.error, 'wellbeing')}
      />
      <Rating
        label="Энергия"
        value={energy}
        onChange={setEnergy}
        captions={ENERGY_CAPTIONS}
        allowEmpty
        error={fieldError(save.error, 'energy')}
      />
      <Input label="Заметка" value={note} onChange={(e) => setNote(e.target.value)} />
      <p className="text-sm text-muted">Если за этот день уже есть запись, она будет исправлена.</p>
      <ErrorText error={otherError(save.error, 'wellbeing', 'energy')} />
      <Button type="submit" variant="primary" loading={save.isPending}>
        Сохранить
      </Button>
    </form>
  )
}

function ConditionForm({ onDone }: { onDone: () => void }) {
  const toast = useToast()
  const add = useAddCondition()
  const areas = useBodyAreas()
  const [kind, setKind] = useState<ConditionKind>('injury')
  const [title, setTitle] = useState('')
  const [area, setArea] = useState('')
  const [startedOn, setStartedOn] = useState(todayIso())
  const [severity, setSeverity] = useState<number | null>(1)
  const [note, setNote] = useState('')
  const [missing, setMissing] = useState(false)

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!title.trim()) {
      setMissing(true)
      return
    }
    add.mutate(
      {
        kind,
        title: title.trim(),
        started_on: startedOn,
        body_area: area || null,
        severity: severity ?? 1,
        note: note.trim() || null,
      },
      {
        onSuccess: () => {
          toast.show({ message: 'Добавлено', tone: 'accent' })
          onDone()
        },
      },
    )
  }

  return (
    <form onSubmit={submit} className="grid gap-4" noValidate>
      <Select
        label="Вид"
        value={kind}
        onChange={(e) => setKind(e.target.value as ConditionKind)}
        options={(Object.keys(KIND_LABEL) as ConditionKind[]).map((k) => ({ value: k, label: KIND_LABEL[k] }))}
        error={fieldError(add.error, 'kind')}
      />
      <Input
        label="Название"
        value={title}
        placeholder="Например, растяжение плеча"
        onChange={(e) => {
          setTitle(e.target.value)
          setMissing(false)
        }}
        error={missing ? 'Напиши название' : fieldError(add.error, 'title')}
      />
      <Select
        label="Область тела"
        value={area}
        onChange={(e) => setArea(e.target.value)}
        placeholder="Не указана"
        options={Object.entries(areas.data ?? {}).map(([value, label]) => ({ value, label }))}
        error={fieldError(add.error, 'body_area')}
      />
      <Input
        label="Начало"
        type="date"
        value={startedOn}
        onChange={(e) => setStartedOn(e.target.value)}
        error={fieldError(add.error, 'started_on')}
      />
      <Rating
        label="Тяжесть"
        value={severity}
        onChange={setSeverity}
        captions={['лёгкая', 'средняя', 'тяжёлая']}
        error={fieldError(add.error, 'severity')}
      />
      <Input label="Заметка" value={note} onChange={(e) => setNote(e.target.value)} />
      <ErrorText error={otherError(add.error, 'kind', 'title', 'body_area', 'started_on', 'severity')} />
      <Button type="submit" variant="primary" loading={add.isPending}>
        Добавить
      </Button>
    </form>
  )
}

function ConditionRow({ item, areas }: { item: Condition; areas: Record<string, string> }) {
  const toast = useToast()
  const close = useCloseCondition()
  const remove = useDeleteCondition()
  return (
    <li className="flex items-center gap-3 px-4 py-3">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-fg">{item.title}</span>
          <Badge tone={item.active ? 'warning' : 'neutral'}>{item.active ? 'идёт' : 'закрыто'}</Badge>
        </div>
        <div className="text-xs text-muted">
          {KIND_LABEL[item.kind]}
          {item.body_area ? ` · ${areas[item.body_area] ?? item.body_area}` : ''}
          {` · ${SEVERITY_LABEL[item.severity] ?? ''} тяжесть`}
          {` · с ${formatDate(item.started_on)}`}
          {item.ended_on ? ` по ${formatDate(item.ended_on)}` : ''}
          {item.note ? ` · ${item.note}` : ''}
        </div>
        <ErrorText error={close.error} />
      </div>
      {item.active ? (
        <Button
          size="sm"
          loading={close.isPending}
          onClick={() =>
            close.mutate(
              { id: item.id, ended_on: null },
              { onSuccess: () => toast.show({ message: 'Закрыто сегодняшней датой' }) },
            )
          }
        >
          Закрыть
        </Button>
      ) : null}
      <DeleteButton
        what={item.title}
        pending={remove.isPending}
        error={remove.error}
        onConfirm={() => remove.mutateAsync(item.id).then(() => toast.show({ message: 'Удалено' }))}
      />
    </li>
  )
}

export function WellbeingPage() {
  const wellbeing = useWellbeing()
  const conditions = useConditions()
  const areas = useBodyAreas()
  const removeWellbeing = useDeleteWellbeing()
  const toast = useToast()
  const [wellbeingOpen, setWellbeingOpen] = useState(false)
  const [conditionOpen, setConditionOpen] = useState(false)

  if (wellbeing.isPending || conditions.isPending) return <PageSpinner />
  if (wellbeing.isError) return <ErrorText error={wellbeing.error} />
  if (conditions.isError) return <ErrorText error={conditions.error} />

  const entries = wellbeing.data
  const today = entries.find((e) => e.day === todayIso())
  const wellbeingButton = (
    <Button variant="primary" icon="plus" onClick={() => setWellbeingOpen(true)}>
      {today ? 'Изменить сегодня' : 'Записать самочувствие'}
    </Button>
  )
  const conditionButton = (
    <Button variant={conditions.data.length ? 'secondary' : 'primary'} icon="plus" onClick={() => setConditionOpen(true)}>
      Добавить травму или болезнь
    </Button>
  )

  return (
    <div className="grid gap-6">
      <section>
        <TabToolbar
          description="Раз в день: самочувствие, энергия и заметка. Повторная запись правит дневную."
          action={entries.length ? wellbeingButton : null}
        />
        <Card title="Самочувствие" flush>
          {entries.length === 0 ? (
            <EmptyState
              icon="user"
              title="Самочувствие ещё не отмечено"
              description="Отметь, как ты себя чувствуешь сегодня: оценка от 1 до 5 занимает пару секунд."
              action={wellbeingButton}
            />
          ) : (
            <ul className="divide-y divide-border">
              {entries.slice(0, 30).map((e) => (
                <li key={e.id} className="flex items-center gap-3 px-4 py-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent-soft text-lg font-semibold tabular-nums text-accent">
                    {e.wellbeing}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-fg">
                      {formatDayTitle(e.day)}
                      {e.energy !== null ? ` · энергия ${e.energy}/5` : ''}
                    </div>
                    {e.note ? <div className="text-xs text-muted">{e.note}</div> : null}
                  </div>
                  <DeleteButton
                    what={`самочувствие за ${formatDate(e.day)}`}
                    pending={removeWellbeing.isPending}
                    error={removeWellbeing.error}
                    onConfirm={() => removeWellbeing.mutateAsync(e.day).then(() => toast.show({ message: 'Удалено' }))}
                  />
                </li>
              ))}
            </ul>
          )}
        </Card>
      </section>

      <section>
        <TabToolbar
          description="Активные состояния видны в «Сегодня» и учитываются при планировании тренировок."
          action={conditions.data.length ? conditionButton : null}
        />
        <Card title="Травмы и болезни" flush>
          {conditions.data.length === 0 ? (
            <EmptyState
              icon="alert"
              title="Травм и болезней нет"
              description="Если что-то болит или ты заболел, добавь запись: приложение учтёт это в тренировках и советах."
              action={conditionButton}
            />
          ) : (
            <ul className="divide-y divide-border">
              {conditions.data.map((c) => (
                <ConditionRow key={c.id} item={c} areas={areas.data ?? {}} />
              ))}
            </ul>
          )}
        </Card>
      </section>

      <Sheet open={wellbeingOpen} onClose={() => setWellbeingOpen(false)} title="Самочувствие">
        <WellbeingForm existing={today} onDone={() => setWellbeingOpen(false)} />
      </Sheet>
      <Sheet open={conditionOpen} onClose={() => setConditionOpen(false)} title="Травма или болезнь">
        <ConditionForm onDone={() => setConditionOpen(false)} />
      </Sheet>
    </div>
  )
}
